import { useParams } from 'react-router';

/**
 * Разбор параметров адреса.
 *
 * ПРОСТРАНСТВО И КАБИНЕТ ЖИВУТ В АДРЕСЕ, А НЕ В СОСТОЯНИИ. Это требование
 * НФ-5, доведённое до интерфейса: пока они в пути, их невозможно потерять
 * при переходе и невозможно случайно показать данные не того пространства.
 * Стоит убрать их в состояние React — и появляется мгновение между сменой
 * кабинета и перезапросом данных, когда на экране числа одного кабинета
 * под названием другого.
 *
 * АДРЕС ПРОВЕРЯЕТСЯ, А НЕ ЧИТАЕТСЯ НАИВНО. Адресом делятся в переписке,
 * его правят руками, он приходит из закладки полугодовой давности. Мусор
 * в нём должен превращаться в понятный отказ здесь, а не в загадочный
 * ответ базы двумя слоями ниже.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПРОВЕРКА СВОЯ, А НЕ ЧЕРЕЗ zod, И ЭТО РЕШЕНИЕ ПРО ВЕС.
 *
 * Здесь стоял `z.object({ wsId: z.string().uuid(), … })` — двенадцать
 * знаков смысла, которые тянули В ГЛАВНЫЙ КУСОК СБОРКИ всю библиотеку
 * разбора: 146 КБ исходника, 7,6 % общего куска кабинета. Этот файл
 * читает каркас на каждом экране, значит библиотека ехала всем и всегда.
 *
 * Вопрос, на который она отвечала, ровно один: «две ли это строки вида
 * UUID». Он решается выражением в одну строку, и решается тем же
 * способом, каким его решает сама библиотека.
 *
 * ГДЕ zod ОСТАЁТСЯ — там, где он по делу: разбор отборов на экране
 * запросов (`clusters/filters.ts`), где схема большая, а сам экран
 * едет отдельным куском.
 * ═══════════════════════════════════════════════════════════════════════
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function опознаватель(v: unknown): v is string {
  return typeof v === 'string' && UUID.test(v);
}

export interface CabinetParams {
  wsId: string;
  cabinetId: string;
}

/** Разобранные параметры — или `null`, если в адресе не то. */
export function useCabinetParams(): CabinetParams | null {
  const { wsId, cabinetId } = useParams();
  if (!опознаватель(wsId) || !опознаватель(cabinetId)) return null;
  return { wsId, cabinetId };
}

/** Адрес кабинета. Собирается в одном месте, чтобы не набираться руками. */
export function cabinetPath(wsId: string, cabinetId: string, tail = ''): string {
  return `/rk/${wsId}/${cabinetId}${tail}`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОСЛЕДНИЙ ОТКРЫТЫЙ КАБИНЕТ — ЧТОБЫ БЫЛО КУДА ВЕРНУТЬСЯ.
 *
 * Экран выбора кабинета живёт по адресу `/rk/:ws`, где кабинета нет
 * вовсе. В эталоне на нём стоит «Назад в биддер» — и это верно для
 * того, кто пришёл сюда из шапки («Все кабинеты и ключи»): он вышел
 * из кабинета на минуту и хочет вернуться. Но подставить туда первый
 * попавшийся кабинет нельзя — это увело бы его в чужой.
 *
 * Поэтому рамка кабинета запоминает, в каком кабинете человек был,
 * а экран выбора читает это и рисует кнопку, только если помнит.
 * Тот, кто вошёл впервые, кнопки не увидит — ему и правда некуда.
 *
 * ПАМЯТЬ НА УСТРОЙСТВЕ, А НЕ НА СЕРВЕРЕ: это свойство вкладки,
 * а не учётной записи. Синхронизировать её между устройствами
 * значило бы менять человеку кнопку там, где он ничего не трогал.
 *
 * ХРАНИЛИЩЕ МОЖЕТ БЫТЬ ЗАПРЕЩЕНО (приватное окно, настройки
 * браузера) — тогда чтение и запись просто ничего не делают,
 * а кнопки не будет. Это не поломка: кнопка — удобство, а не дорога.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ПАМЯТЬ_КАБИНЕТА = 'bidra.последний.кабинет';

export function запомнитьКабинет(wsId: string, cabinetId: string): void {
  try {
    localStorage.setItem(`${ПАМЯТЬ_КАБИНЕТА}.${wsId}`, cabinetId);
  } catch {
    /* Приватное окно и запрет хранилища — не повод падать. */
  }
}

export function последнийКабинет(wsId: string): string | null {
  try {
    return localStorage.getItem(`${ПАМЯТЬ_КАБИНЕТА}.${wsId}`);
  } catch {
    return null;
  }
}
